import { useI18n } from '../locales/I18n';
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Story } from '../stories';
import { StoryCard } from './StoryCard';
import type { StoryActions } from './StoryCard';
export function StoryShelf({ title, subtitle, items, kind = 'category', icon, empty, thirdItem, ...actions }: StoryActions & {
    title: string;
    subtitle?: string;
    items: Story[];
    kind?: 'category' | 'continue' | 'recommended';
    icon?: ReactNode;
    empty?: ReactNode;
    thirdItem?: ReactNode;
}) {
    const { t,language } = useI18n();
    const rtl=language==='ar';
    const rail = useRef<HTMLDivElement>(null);
    const [pagination, setPagination] = useState({ stops: [0], active: 0 });
    const touch = useRef<{
        x: number;
        y: number;
        edge: number;
    } | null>(null);
    const update = () => {
        const el = rail.current;
        if (!el)
            return;
        const cards = [...el.children].filter(child=>(child as HTMLElement).getClientRects().length>0) as HTMLElement[];
        if(!cards.length){setPagination({stops:[0],active:0});return;}
        const max = Math.max(0, el.scrollWidth - el.clientWidth);
        const stride = cards.length > 1 ? Math.abs(cards[1].offsetLeft - cards[0].offsetLeft) : el.clientWidth;
        const perPage = Math.max(1, Math.floor(el.clientWidth / stride));
        const stops = [0];
        for (let i = perPage; i < cards.length && stops.at(-1)! < max - 2; i += perPage) {
            stops.push(Math.min(max, Math.abs(cards[i].offsetLeft - cards[0].offsetLeft)));
        }
        if (max > 2 && stops.at(-1)! < max - 2)
            stops.push(max);
        const position=rtl?-el.scrollLeft:el.scrollLeft;
        const active = stops.reduce((best, stop, index) => Math.abs(stop - position) < Math.abs(stops[best] - position) ? index : best, 0);
        setPagination({ stops, active });
    };
    useEffect(() => {
        if (!rail.current)
            return;
        const observer = new ResizeObserver(update);
        rail.current.scrollTo({left:0,behavior:'instant'});
        const watch=()=>{observer.disconnect();observer.observe(rail.current!);for(const child of rail.current!.children)observer.observe(child);update();};
        // Consent/no-fill can insert or hide the ad without changing the rail's width.
        const mutations=new MutationObserver(watch);
        mutations.observe(rail.current,{childList:true,subtree:true,attributes:true,attributeFilter:['hidden']});
        watch();
        return () => {observer.disconnect();mutations.disconnect();};
    }, [items.map(item => item.id).join(','),rtl]);
    function go(index: number) {
        const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
        const page = (index + pagination.stops.length) % pagination.stops.length;
        rail.current?.scrollTo({ left: (rtl?-1:1)*pagination.stops[page], behavior: reduced ? 'instant' : 'smooth' });
    }
    return <section className="shelf" data-kind={kind} aria-label={t(title)} id={kind === 'recommended' ? 'recommended' : undefined}>
    <div className="shelf-heading"><div><h2>{icon && <span className="shelf-icon">{icon}</span>}{t(title)}</h2>{subtitle && <p>{t(subtitle)}</p>}</div>
      {pagination.stops.length > 1 && <div className="shelf-controls"><button aria-label={t(rtl?"Scroll {0} right":"Scroll {0} left", { "0": t(title) })} onClick={() => go(pagination.active - 1)}><ArrowLeft size={16}/></button><button aria-label={t(rtl?"Scroll {0} left":"Scroll {0} right", { "0": t(title) })} onClick={() => go(pagination.active + 1)}><ArrowRight size={16}/></button></div>}
    </div>
    {items.length ? <><div className="story-rail" dir={rtl?'rtl':'ltr'} ref={rail} onScroll={update} onTouchStart={event => { const el = rail.current!; const point = event.touches[0]; touch.current = { x: point.clientX, y: point.clientY, edge: Math.abs(el.scrollLeft) < 2 ? -1 : Math.abs(el.scrollLeft) + el.clientWidth >= el.scrollWidth - 2 ? 1 : 0 }; }} onTouchEnd={event => { const start = touch.current; touch.current = null; if (!start || pagination.stops.length < 2)
            return; const point = event.changedTouches[0]; const dx = point.clientX - start.x; if (Math.abs(dx) < 55 || Math.abs(dx) < Math.abs(point.clientY - start.y) * 1.5)
            return; if (start.edge === 1 && (rtl?dx>0:dx<0))
            go(0);
        else if (start.edge === -1 && (rtl?dx<0:dx>0))
            go(pagination.stops.length - 1); }} onTouchCancel={() => { touch.current = null; }}>{items.slice(0,2).map(item => <StoryCard key={item.id} item={item} {...actions} resume={kind === 'continue'}/>)}{kind==='category'&&items.length>=2&&thirdItem}{items.slice(2).map(item => <StoryCard key={item.id} item={item} {...actions} resume={kind === 'continue'}/>)}</div>
      {kind!=='category' && pagination.stops.length > 1 && <div className="shelf-dots" role="group" aria-label={t("{0} pages", { "0": t(title) })}>{pagination.stops.map((_, index) => <button key={index} aria-label={t("Show {0} page {1}", { "0": t(title), "1": t(index + 1) })} aria-current={index === pagination.active ? 'true' : undefined} onClick={() => go(index)}><span /></button>)}</div>}
    </> : empty ?? <div className="shelf-empty">{t("This trail is still growing. More true stories are on the way.")}</div>}
  </section>;
}
